import { Commit, Dispatch } from 'vuex'
import { isApiError } from '@/types/api'
import {
  cancelVideoProcessingRun,
  getVideoProcessingRun,
  subscribeVideoProcessingRun,
  uploadVideoToWorkflowRun
} from '@/utils/api'
import type { UserAgentConfigs } from '@/types/agents'
import type {
  VideoProcessingOptionsInput,
  VideoProcessingRunRecord,
  VideoProcessingStreamFrame,
  VideoToWorkflowUploadRequest,
  WorkflowVideoReviewDraft
} from '@/types/videoProcessing'
import {
  isVideoProcessingCancelledFrame,
  isVideoProcessingResultFrame,
  isVideoProcessingRunErrorFrame,
  isVideoProcessingSnapshotFrame,
  isVideoProcessingStatusFrame,
  isVideoProcessingTransportErrorFrame,
  isVideoToWorkflowReportResponse
} from '@/types/videoProcessing'

export interface VideoProcessingState {
  activeRun: VideoProcessingRunRecord | null
  uploading: boolean
  errorMessage: string | null
  reviewDraft: WorkflowVideoReviewDraft | null
}

interface VideoProcessingRootGetters {
  titleAgent: string | null
  agentConfigs: UserAgentConfigs
}

let activeUnsubscribe: (() => void) | null = null
let activeSubscribedRunId: string | null = null
let terminalRunErrorSeen = false

function clearActiveSubscription(): void {
  if (activeUnsubscribe) {
    activeUnsubscribe()
  }
  activeUnsubscribe = null
  activeSubscribedRunId = null
  terminalRunErrorSeen = false
}

function resolveVideoModelName(rootGetters: VideoProcessingRootGetters): string {
  const titleAgentName = rootGetters.titleAgent
  if (!titleAgentName) {
    throw new Error('Title agent is not configured. Set up the configuration assistant first.')
  }

  const agent = rootGetters.agentConfigs[titleAgentName]
  if (!agent) {
    throw new Error(`Title agent "${titleAgentName}" not found in agent configs.`)
  }

  const model = agent.config.model
  if (typeof model !== 'string' || model.trim().length === 0) {
    throw new Error(`Title agent "${titleAgentName}" has no model configured.`)
  }

  return model
}

function buildReviewDraft(runId: string, markdown: string, updatedAt: number): WorkflowVideoReviewDraft {
  return {
    runId,
    markdown,
    updatedAt
  }
}

export const videoProcessing = () => ({
  state: <VideoProcessingState>{
    activeRun: null,
    uploading: false,
    errorMessage: null,
    reviewDraft: null
  },
  mutations: {
    setUploading(state: VideoProcessingState, value: boolean) {
      state.uploading = value
    },
    setActiveRun(state: VideoProcessingState, run: VideoProcessingRunRecord | null) {
      state.activeRun = run
    },
    setError(state: VideoProcessingState, message: string | null) {
      state.errorMessage = message
    },
    setReviewDraft(state: VideoProcessingState, draft: WorkflowVideoReviewDraft | null) {
      state.reviewDraft = draft
    }
  },
  actions: {
    async startWorkflowVideoRun(
      { commit, dispatch, rootGetters }: { commit: Commit; dispatch: Dispatch; rootGetters: VideoProcessingRootGetters },
      payload: { file: File; options?: VideoProcessingOptionsInput }
    ): Promise<string | null> {
      clearActiveSubscription()
      commit('setError', null)
      commit('setReviewDraft', null)
      commit('setUploading', true)

      let modelName: string
      try {
        modelName = resolveVideoModelName(rootGetters)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to resolve workflow-video model.'
        commit('setUploading', false)
        commit('setError', message)
        await dispatch('updateSnackbar', { text: message, timeout: 5000 }, { root: true })
        return null
      }

      const request: VideoToWorkflowUploadRequest = {
        model: modelName,
        ...(payload.options ? { options: payload.options } : {})
      }
      const response = await uploadVideoToWorkflowRun(payload.file, request)
      commit('setUploading', false)

      if (isApiError(response)) {
        commit('setError', response.message)
        await dispatch('updateSnackbar', { text: `Workflow video upload failed: ${response.message}`, timeout: 5000 }, { root: true })
        await dispatch('addLogEvent', { title: 'Workflow Video Upload Failed', content: response }, { root: true })
        return null
      }

      commit('setActiveRun', response)
      await dispatch('subscribeToActiveRun')
      return response.runId
    },

    async subscribeToActiveRun(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: VideoProcessingState }
    ): Promise<void> {
      const run = state.activeRun
      if (!run) {
        return
      }

      if (run.status === 'completed' || run.status === 'error' || run.status === 'cancelled') {
        if (run.status === 'completed' && !state.reviewDraft && run.result && isVideoToWorkflowReportResponse(run.result)) {
          const markdown = run.result.rawSynthesis?.trim() ?? ''
          if (markdown.length > 0) {
            commit('setReviewDraft', buildReviewDraft(run.runId, markdown, run.updatedAt))
          }
        }
        return
      }

      if (activeSubscribedRunId === run.runId && activeUnsubscribe) {
        return
      }

      clearActiveSubscription()
      activeSubscribedRunId = run.runId
      activeUnsubscribe = subscribeVideoProcessingRun(run.runId, {
        onFrame: (frame: VideoProcessingStreamFrame) => {
          if (isVideoProcessingSnapshotFrame(frame)) {
            commit('setActiveRun', frame.record)
            return
          }

          if (isVideoProcessingStatusFrame(frame)) {
            const current = state.activeRun
            if (!current || current.runId !== frame.runId) {
              return
            }
            commit('setActiveRun', {
              ...current,
              status: frame.status,
              updatedAt: frame.updatedAt
            })
            return
          }

          if (isVideoProcessingResultFrame(frame)) {
            const current = state.activeRun
            if (!current || current.runId !== frame.runId) {
              return
            }
            const updatedRun: VideoProcessingRunRecord = {
              ...current,
              status: 'completed',
              result: frame.result,
              updatedAt: frame.updatedAt,
              completedAt: frame.updatedAt
            }
            commit('setActiveRun', updatedRun)
            if (current.kind === 'video_to_workflow' && isVideoToWorkflowReportResponse(frame.result)) {
              const markdown = frame.result.rawSynthesis?.trim() ?? ''
              if (markdown.length === 0) {
                const message = 'Workflow video completed without a reviewable workflow document.'
                commit('setError', message)
                void dispatch('updateSnackbar', { text: message, timeout: 5000 }, { root: true })
              } else {
                commit('setReviewDraft', buildReviewDraft(frame.runId, markdown, frame.updatedAt))
              }
            } else {
              const message = 'Server returned a non-workflow result for a workflow-video run.'
              commit('setError', message)
              void dispatch('updateSnackbar', { text: message, timeout: 5000 }, { root: true })
            }
            return
          }

          if (isVideoProcessingRunErrorFrame(frame)) {
            terminalRunErrorSeen = true
            const current = state.activeRun
            if (current && current.runId === frame.runId) {
              commit('setActiveRun', {
                ...current,
                status: 'error',
                errorMessage: frame.error.message,
                errorStage: frame.error.stage,
                failedBatchIndex: frame.error.failedBatchIndex,
                attemptCount: frame.error.attemptCount,
                partialResult: frame.error.partialReport,
                updatedAt: frame.updatedAt,
                completedAt: frame.updatedAt
              })
            }
            commit('setError', frame.error.message)
            void dispatch('updateSnackbar', { text: `Workflow video failed: ${frame.error.message}`, timeout: 6000 }, { root: true })
            return
          }

          if (isVideoProcessingCancelledFrame(frame)) {
            const current = state.activeRun
            if (current && current.runId === frame.runId) {
              commit('setActiveRun', {
                ...current,
                status: 'cancelled',
                errorMessage: frame.message,
                updatedAt: frame.updatedAt,
                cancelledAt: frame.updatedAt
              })
            }
            commit('setReviewDraft', null)
            return
          }

          if (isVideoProcessingTransportErrorFrame(frame)) {
            if (terminalRunErrorSeen) {
              return
            }
            commit('setError', frame.message)
            void dispatch('updateSnackbar', { text: frame.message, timeout: 5000 }, { root: true })
          }
        },
        onTransportError: (message: string) => {
          if (terminalRunErrorSeen) {
            return
          }
          commit('setError', message)
          void dispatch('updateSnackbar', { text: message, timeout: 5000 }, { root: true })
        },
        onClose: () => {
          activeUnsubscribe = null
          activeSubscribedRunId = null
          terminalRunErrorSeen = false
        }
      })
    },

    async cancelActiveRun({ commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: VideoProcessingState }): Promise<void> {
      const run = state.activeRun
      if (!run) {
        return
      }

      clearActiveSubscription()
      const response = await cancelVideoProcessingRun(run.runId)
      if (isApiError(response)) {
        commit('setError', response.message)
        await dispatch('updateSnackbar', { text: `Cancel failed: ${response.message}`, timeout: 5000 }, { root: true })
        return
      }

      commit('setActiveRun', response)
      commit('setReviewDraft', null)
    },

    async dismissWorkflowReviewDraft({ commit, state }: { commit: Commit; state: VideoProcessingState }): Promise<void> {
      clearActiveSubscription()
      commit('setReviewDraft', null)
      commit('setError', null)
      const run = state.activeRun
      if (run && (run.status === 'completed' || run.status === 'error' || run.status === 'cancelled')) {
        commit('setActiveRun', null)
      }
    },

    async refreshActiveRun({ commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: VideoProcessingState }): Promise<void> {
      const run = state.activeRun
      if (!run) {
        return
      }

      const response = await getVideoProcessingRun(run.runId)
      if (isApiError(response)) {
        commit('setError', response.message)
        await dispatch('updateSnackbar', { text: response.message, timeout: 5000 }, { root: true })
        return
      }

      commit('setActiveRun', response)
      if (response.status === 'completed' && response.result && isVideoToWorkflowReportResponse(response.result)) {
        const markdown = response.result.rawSynthesis?.trim() ?? ''
        if (markdown.length > 0) {
          commit('setReviewDraft', buildReviewDraft(response.runId, markdown, response.updatedAt))
        }
      }
    }
  },
  getters: {
    videoProcessingActiveRun: (state: VideoProcessingState): VideoProcessingRunRecord | null => state.activeRun,
    videoProcessingUploading: (state: VideoProcessingState): boolean => state.uploading,
    videoProcessingError: (state: VideoProcessingState): string | null => state.errorMessage,
    videoProcessingReviewDraft: (state: VideoProcessingState): WorkflowVideoReviewDraft | null => state.reviewDraft,
    videoProcessingHasActiveRun: (state: VideoProcessingState): boolean =>
      state.uploading || state.activeRun?.status === 'queued' || state.activeRun?.status === 'running',
    videoProcessingHasPendingReview: (state: VideoProcessingState): boolean => state.reviewDraft != null
  }
})
